import React from 'react'
import {
  BogoDevicesFeature,
  BogoFeaturesTitle,
  BogoPricingRows,
  BogoMatrixHeader,
} from '@mc/bogo'
import { useBogo } from '@mc/bogo-utils'
import { Conditional, ShowIf } from '@mc/conditional-rendering'
import { useTranslation } from '@mc/i18n'
import { Table, TableFeature, TableHeader, usePlansContext } from '@mc/plans-ui'
import { usePromotion } from '@mc/promo-utils'
import { PromoMatrixHeader } from '@mc/promos'
import {
  BundlingToolkitAndCertificatesMatrixFeatures,
  BundlingToolkitAndCertificatesMatrixHeader,
  useBundlingToolkitAndCertificatesExperiment,
  useTrackBundlingToolkitAndCertificatesExperimentViewed,
} from '../../experiments/prcng-698_v1_bundling_toolkit_and_certificates'
import {
  PlanZFreeGiftMatrixFeatures,
  usePlanZFreeGiftExperiment,
} from '../../experiments/prcng-955_v1_plan_z_bogo'
import {
  usePlusBadges,
  useTrackPlusBadgeExperimentViewed,
} from '../../experiments/prcng-1109_v1_plus_badge'
import {
  MCProMatrix,
  useMCProExperiment,
} from '../../experiments/prcng-1194_v1_mc_pro_tier'
import { CHECKOUT_I18N_NAMESPACE } from '../../i18n'
import { RegularHeader } from './components/RegularHeader'
import {
  DevicesFeature,
  OfflineDownloadFeature,
  PricingFeature,
} from './features'
import { useDefaultPlanSelection } from './useDefaultPlanSelection'

export const Matrix = () => {
  const { t } = useTranslation(CHECKOUT_I18N_NAMESPACE)
  const { isVariant: isMCProVariant } = useMCProExperiment()

  useTrackBundlingToolkitAndCertificatesExperimentViewed()
  useDefaultPlanSelection()

  if (isMCProVariant) return <MCProMatrix />

  return (
    <Table label={t('plans.label')}>
      <TableHeader.Header>
        <HeaderContent />
      </TableHeader.Header>

      <TableFeature.FeatureList>
        <FeaturesContent />
      </TableFeature.FeatureList>
    </Table>
  )
}

const HeaderContent = () => {
  const { isGift } = usePlansContext()
  const { isPromotionOn } = usePromotion({ isGift })
  const { isBogoOfferEligible } = useBogo({ isGift })
  const { isVariant: isBundlingToolkitAndCertificatesOn } =
    useBundlingToolkitAndCertificatesExperiment()
  const { duoBadge, familyBadge } = usePlusBadges()

  // Exposure fires only on the two headers this experiment badges — promo
  // and regular. Conditional priority is BOGO > promo > prcng-698_v1 >
  // regular, so promo still wins when prcng-698_v1 is also on; gate on promo
  // before ruling prcng-698_v1 out.
  useTrackPlusBadgeExperimentViewed(
    !isBogoOfferEligible &&
      (isPromotionOn || !isBundlingToolkitAndCertificatesOn),
  )

  // The promo header owns its own "Best Value" string, so it takes the arm's
  // intent rather than the string.
  const hidePromoFamilyBadge = !familyBadge

  return (
    <Conditional>
      <ShowIf condition={isBogoOfferEligible}>
        <BogoMatrixHeader />
      </ShowIf>
      <ShowIf condition={isPromotionOn}>
        <PromoMatrixHeader
          duoBadge={duoBadge}
          hideFamilyBadge={hidePromoFamilyBadge}
        />
      </ShowIf>
      <ShowIf condition={isBundlingToolkitAndCertificatesOn}>
        <BundlingToolkitAndCertificatesMatrixHeader />
      </ShowIf>
      <ShowIf isDefault>
        <RegularHeader />
      </ShowIf>
    </Conditional>
  )
}

const FeaturesContent = () => {
  const { isGift } = usePlansContext()
  const { isBogoOfferEligible } = useBogo({ isGift })
  const { isVariant: isBundlingToolkitAndCertificatesOn } =
    useBundlingToolkitAndCertificatesExperiment()
  const { isVariant: isPlanZFreeGift } = usePlanZFreeGiftExperiment()

  return (
    <Conditional>
      <ShowIf condition={isBogoOfferEligible}>
        <BogoPricingRows />
        <BogoFeaturesTitle />
        <BogoDevicesFeature />
        <OfflineDownloadFeature />
      </ShowIf>
      <ShowIf condition={isBundlingToolkitAndCertificatesOn}>
        <BundlingToolkitAndCertificatesMatrixFeatures />
      </ShowIf>
      <ShowIf condition={isPlanZFreeGift}>
        <PlanZFreeGiftMatrixFeatures />
      </ShowIf>
      <ShowIf isDefault>
        <PricingFeature />
        <DevicesFeature />
        <OfflineDownloadFeature />
      </ShowIf>
    </Conditional>
  )
}
